/**
 * Calendar Service
 *
 * Serviço para comunicação com API de calendário
 */

import { api } from '@/lib/api';
import type {
  CalendarEventResponse,
  CreateCalendarEventRequest,
  UpdateCalendarEventRequest,
  CalendarEventQuery,
  ListResponse
} from '@troia-v3/types';

export const calendarService = {
  /**
   * Listar eventos do calendário
   */
  async getEvents(query?: CalendarEventQuery): Promise<ListResponse<CalendarEventResponse>> {
    const response = await api.get('/calendar', { params: query });
    return response.data.data;
  },

  /**
   * Buscar evento por ID
   */
  async getEventById(id: string): Promise<CalendarEventResponse> {
    const response = await api.get(`/calendar/${id}`);
    return response.data.data;
  },

  /**
   * Criar novo evento
   */
  async createEvent(data: CreateCalendarEventRequest): Promise<CalendarEventResponse> {
    const response = await api.post('/calendar', data);
    return response.data.data;
  },

  /**
   * Atualizar evento existente
   */
  async updateEvent(id: string, data: UpdateCalendarEventRequest): Promise<CalendarEventResponse> {
    const response = await api.put(`/calendar/${id}`, data);
    return response.data.data;
  },

  /**
   * Deletar evento
   */
  async deleteEvent(id: string): Promise<void> {
    await api.delete(`/calendar/${id}`);
  },

  /**
   * Buscar eventos do usuário logado
   */
  async getMyEvents(query?: CalendarEventQuery): Promise<ListResponse<CalendarEventResponse>> {
    const response = await api.get('/calendar/my', { params: query });
    return response.data.data;
  },

  /**
   * Buscar eventos em um intervalo de tempo
   */
  async getEventsInRange(
    startTime: string,
    endTime: string,
    userId?: string,
    statuses?: string[]  // ✅ CSV enviado ao backend; ausente = default (esconde cancelados)
  ): Promise<CalendarEventResponse[]> {
    const response = await api.get('/calendar/range', {
      params: {
        startTime,
        endTime,
        userId,
        ...(statuses && statuses.length > 0 ? { statuses: statuses.join(',') } : {})
      }
    });
    return response.data.data;
  },

  /**
   * Buscar eventos por atividade
   */
  async getEventByActivityId(activityId: string): Promise<CalendarEventResponse> {
    const response = await api.get(`/calendar/activity/${activityId}`);
    return response.data.data;
  },

  /**
   * Buscar eventos com participante específico
   */
  async getEventsWithAttendee(contactId?: string, userId?: string): Promise<CalendarEventResponse[]> {
    const response = await api.get('/calendar/attendee', {
      params: { contactId, userId }
    });
    return response.data.data;
  },

  /**
   * Buscar instâncias de evento recorrente
   */
  async getRecurringInstances(recurringEventId: string): Promise<CalendarEventResponse[]> {
    const response = await api.get(`/calendar/recurring/${recurringEventId}/instances`);
    return response.data.data;
  },

  /**
   * Buscar eventos pendentes de sincronização
   */
  async getPendingSyncEvents(): Promise<CalendarEventResponse[]> {
    const response = await api.get('/calendar/pending-sync');
    return response.data.data;
  },

  /**
   * Atualizar status de sincronização
   */
  async updateSyncStatus(
    id: string,
    integrationId: string,
    syncStatus: string,
    syncError?: string
  ): Promise<void> {
    await api.put(`/calendar/${id}/sync-status`, {
      integrationId,
      syncStatus,
      syncError
    });
  },

  /**
   * Criar follow-up vinculado a um contato
   */
  async createFollowUp(data: any): Promise<CalendarEventResponse> {
    const response = await api.post('/calendar/follow-ups', data);
    return response.data.data;
  },

  /**
   * Buscar follow-ups de um contato específico
   */
  async getFollowUpsByContact(contactId: string): Promise<CalendarEventResponse[]> {
    const response = await api.get(`/calendar/follow-ups/contact/${contactId}`);
    return response.data.data;
  },

  /**
   * Atualizar follow-up existente
   */
  async updateFollowUp(id: string, data: any): Promise<CalendarEventResponse> {
    const response = await api.put(`/calendar/follow-ups/${id}`, data);
    return response.data.data;
  },

  /**
   * Deletar follow-up
   */
  async deleteFollowUp(id: string): Promise<void> {
    await api.delete(`/calendar/follow-ups/${id}`);
  }
};
